<template>
  <div class="terrain-attachment-editor" :class="{ 'terrain-attachment-editor--modal': showHeader }">
    <header v-if="showHeader" class="terrain-attachment-editor__header">
      <span class="terrain-attachment-editor__title">{{ label || question.label }}</span>
      <button type="button" class="terrain-attachment-editor__close" aria-label="Cerrar" @click="$emit('close')">
        <font-awesome-icon icon="times" />
      </button>
    </header>

    <div class="terrain-attachment-editor__body">
      <ul v-if="uploads.length" class="terrain-attachment-field__list">
        <li v-for="upload in uploads" :key="upload.key" class="terrain-attachment-field__item" :class="`terrain-attachment-field__item--${upload.status}`">
          <div class="terrain-attachment-field__thumb-wrap">
            <img v-if="upload.previewUrl" :src="upload.previewUrl" class="terrain-attachment-field__thumb" alt="">
            <font-awesome-icon v-else icon="file-alt" class="terrain-attachment-field__file-icon" />
          </div>
          <div class="terrain-attachment-field__item-body">
            <span class="terrain-attachment-field__name">{{ upload.name }}</span>
            <span v-if="upload.status === 'uploading'" class="terrain-attachment-field__status">
              <font-awesome-icon icon="spinner" spin /> Subiendo…
            </span>
            <span v-else-if="upload.status === 'error'" class="terrain-attachment-field__status terrain-attachment-field__status--error">
              <font-awesome-icon icon="exclamation-triangle" /> {{ upload.error }}
            </span>
            <span v-else class="terrain-attachment-field__status terrain-attachment-field__status--done">
              <font-awesome-icon icon="check" /> Subido
            </span>
          </div>
          <button
            v-if="upload.status !== 'uploading'"
            type="button"
            class="terrain-attachment-field__remove"
            aria-label="Quitar adjunto"
            @click="removeUpload(upload)"
          >
            <font-awesome-icon icon="trash-alt" />
          </button>
        </li>
      </ul>

      <div
        class="terrain-attachment-field__dropzone"
        :class="{ 'terrain-attachment-field__dropzone--active': isDragging, 'terrain-attachment-field__dropzone--disabled': atLimit }"
        tabindex="0"
        role="button"
        :aria-disabled="atLimit"
        :aria-label="`Elegir ${question.type === 'photo' ? 'fotos' : 'archivos'}`"
        @click="openFilePicker"
        @keydown.enter="openFilePicker"
        @keydown.space.prevent="openFilePicker"
        @dragenter.prevent="onDragEnter"
        @dragover.prevent="onDragEnter"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
      >
        <font-awesome-icon icon="cloud-upload-alt" class="terrain-attachment-field__dropzone-icon" />
        <p class="terrain-attachment-field__dropzone-text">
          <strong>Arrastra {{ question.type === 'photo' ? 'fotos' : 'archivos' }} aquí</strong> o haz clic para elegir
        </p>
        <p class="terrain-attachment-field__dropzone-hint">Hasta {{ maxPerQuestion }} {{ question.type === 'photo' ? 'imágenes' : 'archivos' }} por pregunta · {{ acceptHint }}</p>
      </div>
      <input
        ref="fileInput"
        class="terrain-attachment-field__hidden-input"
        type="file"
        multiple
        :accept="accept"
        :disabled="atLimit"
        @change="onFilesSelected"
      >

      <button
        v-if="question.type === 'photo'"
        type="button"
        class="terrain-attachment-field__camera-btn"
        :disabled="atLimit"
        @click="openCamera"
      >
        <font-awesome-icon icon="camera" /> Tomar foto
      </button>
      <input
        v-if="question.type === 'photo'"
        ref="cameraInput"
        class="terrain-attachment-field__hidden-input"
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        :disabled="atLimit"
        @change="onFilesSelected"
      >

      <p v-if="atLimit" class="terrain-error-text">Alcanzó el máximo de {{ maxPerQuestion }} {{ question.type === 'photo' ? 'imágenes' : 'adjuntos' }} para esta pregunta.</p>
    </div>
  </div>
</template>

<script>
import { createTerrainApi } from '@/services/terrain/api.js';

// AttachmentEditor concentra la subida/quita interactiva de una respuesta
// file/photo: sube inmediatamente al elegir el archivo (Proceso 1,
// status=valid) y emite {attachmentIds:[...], attachmentNames:{id:name}}
// como value real de la pregunta. Se usa en dos contextos (AttachmentField.vue):
// inline durante el llenado del formulario (showHeader=false, sin chrome de
// modal) y dentro de un modal dinámico durante la edición de una corrección
// (showHeader=true) — onChange es el callback-prop que reemplaza a $emit
// ahí, porque un modal dinámico de vue-js-modal no reenvía los $emit
// custom del componente montado (solo sus propios eventos de ciclo de vida
// y el 'close' especial, que sí cierra el modal automáticamente).
export default {
  name: 'AttachmentEditor',
  props: {
    question: { type: Object, required: true },
    value: { default: null },
    campaignId: { type: String, default: null },
    templateVersionId: { type: String, default: null },
    showHeader: { type: Boolean, default: false },
    label: { type: String, default: null },
    onChange: { type: Function, default: null },
  },
  data() {
    return { uploads: [], sequence: 0, isDragging: false };
  },
  computed: {
    accept() { return this.question.type === 'photo' ? 'image/jpeg,image/png,image/webp' : '.pdf,.docx,.xlsx,.csv'; },
    acceptHint() { return this.question.type === 'photo' ? 'JPG, PNG o WebP' : 'PDF, DOCX, XLSX o CSV'; },
    maxPerQuestion() { return this.question.type === 'photo' ? 5 : 10; },
    attachmentIds() { return (this.value && this.value.attachmentIds) || []; },
    attachmentNames() { return (this.value && this.value.attachmentNames) || {}; },
    atLimit() { return this.uploads.filter((upload) => upload.status !== 'error').length >= this.maxPerQuestion; },
    terrainApi() { return createTerrainApi(this.$http, this.$store.getters.backendurl); },
  },
  watch: {
    // Reconstruye la lista visible a partir de los ids ya vinculados al
    // value (por ejemplo, al reabrir un borrador guardado o al abrir el
    // editor de una corrección con adjuntos ya vinculados) sin volver a
    // subir nada.
    attachmentIds: {
      immediate: true,
      handler(ids) {
        const known = new Set(this.uploads.filter((upload) => upload.attachmentId).map((upload) => upload.attachmentId));
        ids.filter((id) => !known.has(id)).forEach((id) => {
          this.uploads.push({
            key: `restored-${id}`,
            name: this.attachmentNames[id] || 'Adjunto guardado',
            status: 'done',
            attachmentId: id,
            previewUrl: this.question.type === 'photo' ? this.contentUrl(id) : null,
          });
        });
      },
    },
  },
  beforeDestroy() {
    this.uploads.forEach((upload) => {
      if (upload.previewUrl && upload.key.startsWith('upload-')) URL.revokeObjectURL(upload.previewUrl);
    });
  },
  methods: {
    contentUrl(attachmentId) { return this.terrainApi.getAttachmentContentUrl(attachmentId); },
    openFilePicker() { if (!this.atLimit) this.$refs.fileInput.click(); },
    openCamera() { if (!this.atLimit) this.$refs.cameraInput.click(); },
    onDragEnter() { if (!this.atLimit) this.isDragging = true; },
    onDragLeave() { this.isDragging = false; },
    async onDrop(event) {
      this.isDragging = false;
      await this.processFiles(Array.from((event.dataTransfer && event.dataTransfer.files) || []));
    },
    async onFilesSelected(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = '';
      await this.processFiles(files);
    },
    async processFiles(files) {
      for (const file of files) {
        if (this.atLimit) break;
        // eslint-disable-next-line no-await-in-loop
        await this.uploadOne(file);
      }
    },
    async uploadOne(file) {
      this.sequence += 1;
      const previewUrl = this.question.type === 'photo' && /^image\//.test(file.type) ? URL.createObjectURL(file) : null;
      const upload = {
        key: `upload-${this.sequence}`, name: file.name, status: 'uploading', attachmentId: null, error: null, previewUrl,
      };
      this.uploads.push(upload);
      const formData = new FormData();
      formData.append('campaignId', this.campaignId);
      formData.append('templateVersionId', this.templateVersionId);
      formData.append('questionId', this.question.questionId);
      formData.append('kind', this.question.type);
      formData.append('file', file);
      try {
        const response = await this.terrainApi.uploadAttachment(formData);
        upload.status = 'done';
        upload.attachmentId = response.data.id;
        this.emitValue([...this.attachmentIds, upload.attachmentId], { ...this.attachmentNames, [upload.attachmentId]: file.name });
      } catch (error) {
        upload.status = 'error';
        upload.error = (error.response && error.response.data && error.response.data.message) || 'No se pudo subir el archivo';
      }
    },
    removeUpload(upload) {
      this.uploads = this.uploads.filter((current) => current !== upload);
      if (upload.previewUrl && upload.key.startsWith('upload-')) URL.revokeObjectURL(upload.previewUrl);
      if (!upload.attachmentId) return;
      const remainingIds = this.attachmentIds.filter((id) => id !== upload.attachmentId);
      const remainingNames = { ...this.attachmentNames };
      delete remainingNames[upload.attachmentId];
      this.emitValue(remainingIds, remainingNames);
    },
    emitValue(attachmentIds, attachmentNames) {
      const value = attachmentIds.length ? { attachmentIds, attachmentNames } : null;
      this.$emit('input', value);
      if (this.onChange) this.onChange(value);
    },
  },
};
</script>

<style scoped lang="scss">
.terrain-attachment-editor--modal {
  background: var(--color-white);
  border-radius: var(--terrain-radius-lg);
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;
  overflow: hidden;
  width: 100%;
}

.terrain-attachment-editor__header {
  align-items: center;
  border-bottom: 1px solid var(--color-line);
  display: flex;
  justify-content: space-between;
  padding: var(--terrain-space-sm) var(--terrain-space-md);
}
.terrain-attachment-editor__title { color: var(--color-navy); font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.terrain-attachment-editor__close {
  background: none; border: 0; color: var(--color-ghost); cursor: pointer; flex-shrink: 0; font-size: 1.1rem; padding: var(--terrain-space-xs);
  &:hover { color: var(--color-navy); }
}

.terrain-attachment-editor__body {
  display: grid;
  gap: var(--terrain-space-sm);
  overflow-y: auto;
  padding: var(--terrain-space-md);
}
.terrain-attachment-editor--modal .terrain-attachment-editor__body { padding: var(--terrain-space-md); }

.terrain-attachment-field__hidden-input { display: none; }

.terrain-attachment-field__dropzone {
  align-items: center;
  background: var(--color-mist);
  border: 1px dashed var(--color-line);
  border-radius: var(--terrain-radius-lg);
  cursor: pointer;
  display: grid;
  gap: var(--terrain-space-xs);
  justify-items: center;
  padding: var(--terrain-space-lg);
  text-align: center;
  transition: background-color var(--terrain-transition), border-color var(--terrain-transition), box-shadow var(--terrain-transition);

  &:hover, &:focus-visible { border-color: var(--color-steel); box-shadow: var(--terrain-shadow-sm); outline: none; }
  &:focus-visible { outline: 2px solid var(--color-steel); outline-offset: 2px; }
}
.terrain-attachment-field__dropzone--active { background: var(--terrain-color-success-light); border-color: var(--color-success); }
.terrain-attachment-field__dropzone--disabled { cursor: not-allowed; opacity: .6; }

.terrain-attachment-field__dropzone-icon { color: var(--color-steel); font-size: 1.6rem; }
.terrain-attachment-field__dropzone-text { color: var(--color-navy); margin: 0; }
.terrain-attachment-field__dropzone-hint { color: var(--color-ghost); font-size: var(--terrain-font-caption); margin: 0; }

.terrain-attachment-field__camera-btn {
  align-items: center;
  background: var(--color-navy);
  border: 1px solid var(--color-navy);
  border-radius: var(--terrain-radius-xl);
  color: var(--color-white);
  cursor: pointer;
  display: inline-flex;
  font-weight: 600;
  gap: var(--terrain-space-xs);
  justify-self: start;
  padding: var(--terrain-space-sm) var(--terrain-space-md);
  transition: background-color var(--terrain-transition), border-color var(--terrain-transition);

  &:hover:not(:disabled) { background: var(--color-steel); border-color: var(--color-steel); }
  &:disabled { cursor: not-allowed; opacity: .6; }
}

.terrain-attachment-field__list { display: grid; gap: var(--terrain-space-xs); list-style: none; margin: 0; padding: 0; }
.terrain-attachment-field__item {
  align-items: center;
  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  display: flex;
  gap: var(--terrain-space-sm);
  padding: var(--terrain-space-xs) var(--terrain-space-sm);
  transition: box-shadow var(--terrain-transition);

  &:hover { box-shadow: var(--terrain-shadow-sm); }
}
.terrain-attachment-field__item--error { border-color: var(--color-danger); }

.terrain-attachment-field__thumb-wrap {
  align-items: center;
  background: var(--color-mist);
  border-radius: var(--terrain-radius-sm);
  display: flex;
  flex-shrink: 0;
  height: 40px;
  justify-content: center;
  overflow: hidden;
  width: 40px;
}
.terrain-attachment-field__thumb-wrap .terrain-attachment-field__thumb { border: 0; border-radius: 0; height: 100%; width: 100%; }
.terrain-attachment-field__file-icon { color: var(--color-steel); }

.terrain-attachment-field__item-body { display: grid; flex: 1; gap: 2px; min-width: 0; }
.terrain-attachment-field__name { color: var(--color-navy); font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.terrain-attachment-field__status { color: var(--color-ghost); font-size: var(--terrain-font-caption); }
.terrain-attachment-field__status--error { color: var(--color-danger); }
.terrain-attachment-field__status--done { color: var(--color-success); }

.terrain-attachment-field__remove {
  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  color: var(--color-danger);
  cursor: pointer;
  flex-shrink: 0;
  padding: var(--terrain-space-xs) var(--terrain-space-sm);
  transition: background-color var(--terrain-transition), border-color var(--terrain-transition);

  &:hover { background: var(--terrain-color-danger-light); border-color: var(--color-danger); }
}

.terrain-attachment-field__thumb {
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  height: 96px;
  object-fit: cover;
  width: 96px;
}
</style>
